import { FirebaseOptions, getApp, getApps, initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, SAMLAuthProvider } from 'firebase/auth';

type TenantStatus = 'active' | 'pending' | 'suspended';

export interface PublicTenantConfig {
  slug: string;
  display_name: string;
  primary_domain: string;
  firebase_project_id?: string | null;
  firebase_config?: (FirebaseOptions & { samlProviderId?: string }) | null;
  status: TenantStatus;
}

const firebaseConfig: FirebaseOptions = {
  apiKey: process.env.FIREBASE_API_KEY || '',
  authDomain: `${process.env.FIREBASE_PROJECT_ID || ''}.firebaseapp.com`,
  databaseURL: `https://${process.env.FIREBASE_PROJECT_ID || ''}-default-rtdb.firebaseio.com`,
  projectId: process.env.FIREBASE_PROJECT_ID || '',
  storageBucket: `${process.env.FIREBASE_PROJECT_ID || ''}.firebasestorage.app`,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.FIREBASE_APP_ID || '',
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let activeTenantConfig: PublicTenantConfig | null = null;

try {
  console.log('Firebase auth required - initializing Firebase...');
  console.log('Firebase config:', {
    ...firebaseConfig,
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'missing',
  });

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.error('Firebase configuration incomplete. Required: FIREBASE_API_KEY, FIREBASE_PROJECT_ID');
    throw new Error('Firebase configuration incomplete');
  }

  app = initializeApp(firebaseConfig);
  console.log('Firebase app initialized');

  auth = getAuth(app);
  console.log('Firebase Auth initialized');
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  app = null;
  auth = null;
}

export { auth };
export { app };
export { activeTenantConfig };

export const isFirebaseInitialized = (): boolean => {
  return auth !== null && app !== null;
};

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

function apiBaseUrl(): string {
  const envApiUrl = ((process.env as any)?.API_BACKEND_BASE_URL || '').trim();
  if (envApiUrl && envApiUrl !== 'undefined' && envApiUrl !== 'null') {
    return envApiUrl.endsWith('/api/v1') ? envApiUrl : `${envApiUrl.replace(/\/$/, '')}/api/v1`;
  }
  return '/api/v1';
}

function normalizeFirebaseConfig(tenant: PublicTenantConfig): FirebaseOptions | null {
  const config = tenant.firebase_config;
  if (!config) {
    return null;
  }

  const projectId = config.projectId || tenant.firebase_project_id || undefined;
  if (!config.apiKey || !config.authDomain || !projectId || !config.appId) {
    return null;
  }

  return {
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    databaseURL: config.databaseURL || `https://${projectId}-default-rtdb.firebaseio.com`,
    projectId,
    storageBucket: config.storageBucket || `${projectId}.firebasestorage.app`,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
    measurementId: config.measurementId,
  };
}

function getOrCreateFirebaseApp(name: string, config: FirebaseOptions): FirebaseApp {
  if (getApps().some((candidate) => candidate.name === name)) {
    return getApp(name);
  }
  return initializeApp(config, name);
}

export async function initializeTenantFirebase(): Promise<PublicTenantConfig | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const host = window.location.hostname.toLowerCase();
  if (!host || isLocalHost(host)) {
    return null;
  }

  const response = await fetch(`${apiBaseUrl()}/tenants/public-config`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Tenant Firebase config lookup failed for ${host}: ${response.status}`);
  }

  const payload = await response.json();
  const tenant = payload?.data as PublicTenantConfig | undefined;
  if (!tenant) {
    throw new Error(`Tenant Firebase config lookup returned no data for ${host}`);
  }
  if (tenant.status !== 'active') {
    throw new Error(`Tenant ${tenant.slug} is not active`);
  }

  const tenantFirebaseConfig = normalizeFirebaseConfig(tenant);
  if (!tenantFirebaseConfig) {
    activeTenantConfig = tenant;
    throw new Error(`Tenant ${tenant.slug} has no complete Firebase web config`);
  }

  app = getOrCreateFirebaseApp(`tenant-${tenant.slug}`, tenantFirebaseConfig);
  auth = getAuth(app);
  activeTenantConfig = tenant;
  console.log(`Firebase initialized for tenant: ${tenant.slug}`);
  return tenant;
}

/** Same SAML setup as dashboard `src/config/firebase.ts` — GCIP Microsoft federation. */
export const getSamlProvider = (): SAMLAuthProvider | null => {
  const providerId =
    (activeTenantConfig?.firebase_config?.samlProviderId || process.env.FIREBASE_SAML_PROVIDER_ID || '').trim();
  if (!providerId || typeof providerId !== 'string') {
    console.warn('Missing FIREBASE_SAML_PROVIDER_ID; Microsoft SSO unavailable');
    return null;
  }
  if (!providerId.startsWith('saml.') || providerId === 'saml.provider-id') {
    console.warn('Invalid FIREBASE_SAML_PROVIDER_ID; expected a real saml.* provider from Firebase');
    return null;
  }
  try {
    return new SAMLAuthProvider(providerId);
  } catch (e) {
    console.error('Failed to initialize SAML provider:', e);
    return null;
  }
};
